import { formatDate } from '@amelio/ui-library';

import type { PerformanceScoreLevel } from '@/types/competency/PerformanceScoreLevel';
import type { EvaluationPeriod } from '@/types/manager-assessment/Evaluation';
import type { PerformanceTrendPoint } from '@/types/performance-profile';
import type { LevelTrendBand, LevelTrendPoint, ScaleBounds } from '@amelio/ui-library';

import { getLocalizedText } from '@/utils/localization';
import { levelForScore } from '@/utils/performanceScoreLevel';

/** The span the server scores a cycle over, and the band the chart plots every mark against. */
export const TREND_SCORE_SCALE: ScaleBounds = { min: 0, max: 100 };

/** The place a second cycle will take, on a trend that holds a single one. */
const NEXT_CYCLE_ID = 'next-cycle';

interface CycleLabelInput {
    point: PerformanceTrendPoint;
    /** The company's evaluation periods; a cycle whose period is not among them keeps its year. */
    periods: EvaluationPeriod[] | undefined;
    language: string;
}

/**
 * What names a cycle under its mark: the period it was evaluated on, and the year it ended in.
 * "Annuel" alone comes back every year and would put two different cycles under one caption.
 */
const cycleLabel = ({ point, periods, language }: CycleLabelInput): string => {
    const name = periods?.find((period) => period.id === point.evaluationPeriodId)?.name;
    const year = point.periodEnd
        ? formatDate({ date: point.periodEnd, format: 'YYYY', language })
        : '';

    return [name, year].filter(Boolean).join(' ');
};

/** A level of the company's scale identifies itself by the floor it starts at. */
const bandId = (level: PerformanceScoreLevel) => level.id ?? `floor-${level.minScore}`;

/**
 * The vertical axis: the company's own performance levels, each at the score it starts at. A
 * level without a floor covers nothing the chart can place, so it is not on the axis.
 */
export const toTrendBands = (levels: PerformanceScoreLevel[]): LevelTrendBand[] =>
    levels
        .filter(
            (level): level is PerformanceScoreLevel & { minScore: number } =>
                level.minScore !== undefined,
        )
        .map((level) => ({
            id: bandId(level),
            from: level.minScore,
            label: getLocalizedText(level.label),
            color: level.color,
        }));

export interface TrendChartPointsInput {
    points: PerformanceTrendPoint[];
    periods: EvaluationPeriod[] | undefined;
    language: string;
    /** Names the empty place a lone cycle is joined to. */
    nextCycleLabel: string;
}

/**
 * The cycles as the chart reads them, oldest first.
 *
 * A single cycle is joined to an empty place named for the one to come, so the shape a trend will
 * take is visible without a second value being invented for it.
 */
export const toTrendChartPoints = ({
    points,
    periods,
    language,
    nextCycleLabel,
}: TrendChartPointsInput): LevelTrendPoint[] => {
    const plotted = points.map((point) => ({
        id: [point.evaluationPeriodId, point.periodStart, point.periodEnd].join('|'),
        value: point.score,
        label: cycleLabel({ point, periods, language }),
    }));

    if (plotted.length !== 1) {
        return plotted;
    }

    return [...plotted, { id: NEXT_CYCLE_ID, value: null, label: nextCycleLabel }];
};

export interface ReachedLevelsInput {
    points: PerformanceTrendPoint[];
    chartPoints: LevelTrendPoint[];
    /** The company's scale, which names what a score reaches. */
    levels: PerformanceScoreLevel[];
}

/**
 * The level each cycle reached, by the id its mark carries — the chart announces it rather than
 * printing it, because the reader was shown a scale and the figure is the arithmetic, not the
 * answer. A score the company's scale does not cover names nothing at all.
 */
export const toReachedLevelLabels = ({
    points,
    chartPoints,
    levels,
}: ReachedLevelsInput): Record<string, string> =>
    Object.fromEntries(
        points.flatMap((point, index) => {
            const reached = levelForScore({ score: point.score, levels });
            const mark = chartPoints[index];

            return reached && mark ? [[mark.id, getLocalizedText(reached.label)]] : [];
        }),
    );

/** The level the most recent cycle reached, whose floor the chart draws behind the curve. */
export const toReachedBandId = (
    points: PerformanceTrendPoint[],
    levels: PerformanceScoreLevel[],
): string | undefined => {
    const latest = points[points.length - 1];
    const reached = latest && levelForScore({ score: latest.score, levels });

    return reached ? bandId(reached) : undefined;
};
